import { useEmbroideryContent } from 'src/hooks/useEmbroideryContent'
import { useEmbroideriesDispatcher } from 'src/hooks/useEmbroideriesDispatcher'
import { isHalfWidthCharChecker } from 'src/logic'
import { Embroidery, EmbroideryKey, I18NText, Locale, PartialActionDispatch, StateObj } from 'src/types'
import { CURSIVE, SANS_SERIF, SERIF } from '../../constants/embroidery'
import { APL_UR002_PLUS_PRODUCT_NUMBER } from '../constants/baseModel'

export const characterCheckHelper = (embroidery: Embroidery, t: I18NText) => {
  const { content } = embroidery
  const isHalfWidthChar = isHalfWidthCharChecker(content)
  // 日本語（漢字・ひらがな・カタカナ）が1文字でも含まれているか判定
  const hasJapaneseChar = (str: string) => /[\u3040-\u30FF\u3400-\u4DBF\u4E00-\u9FFF]/.test(str)
  const hasKoreanChar = (str: string) => /[\uAC00-\uD7AF]/.test(str)
  const isJapanese = hasJapaneseChar(content)
  const isKoreaContents = hasKoreanChar(content)
  const typeFaceJaObjs = t.EMBROIDERY_TYPEFACE_OBJECTS.filter((t) => [SERIF.value].includes(t.value))
  const typeFaceEnObjs = t.EMBROIDERY_TYPEFACE_OBJECTS.filter((t) => [SANS_SERIF.value, CURSIVE.value].includes(t.value))

  // 日本語が1文字でも含まれていた場合は漢字（serif）のみ返す
  const typeFaceObjects = isJapanese ? typeFaceJaObjs : typeFaceEnObjs
  const halfWidthChar = 12
  const fullWidthChar = 6

  return {
    contentMaxLength: isHalfWidthChar ? halfWidthChar : fullWidthChar,
    existsContent: content.length > 0,
    typeFaceObjects,
    isKoreaContents,

    // TODO: ハングル文字が剥き出しになっているの良くないので、ロジックの見直しが必要
    contentCaution: isKoreaContents ? '한자,영어만 자수가능합니다. 한글은 자수 불가입니다.' : ''
  }
}

export const positionObjectGenerator = (productNumber: string, isFirst: boolean, t: I18NText): StateObj[] => {
  const isErmCatcherMitt = productNumber === APL_UR002_PLUS_PRODUCT_NUMBER
  const position1 = isErmCatcherMitt ? t.EMBROIDERY_POSITION_OBJECTS.filter((p) => p.value !== 'thumbFinger') : t.EMBROIDERY_POSITION_OBJECTS
  const position2 = isFirst ? position1.filter((p) => p.value !== 'childFinger') : position1.filter((p) => p.value !== 'pinkyFingerSideBack')
  return position2
}

export const useAtomsEmbroideriesDispatchGenerator = (dispatch: PartialActionDispatch, embroideries: Embroidery[], t: I18NText) => {
  const atomsEmbroideriesDispatcher = (key: EmbroideryKey) => {
    const atomsObjects = {
      position: t.EMBROIDERY_POSITION_OBJECTS,
      typeFace: t.EMBROIDERY_TYPEFACE_OBJECTS,
      color: t.EMBROIDERY_COLOR_OBJECTS,
      shadowColor: t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS,
      edgeColor: t.EMBROIDERY_SHADOW_EDGE_COLOR_OBJECTS
    }
    return (selected: string, i?: number) => useEmbroideriesDispatcher(dispatch, embroideries, selected, atomsObjects, key, i)
  }
  const handleContentAndNum = useEmbroideryContent(dispatch, embroideries)
  return {
    handle: {
      typeFace: atomsEmbroideriesDispatcher('typeFace'),
      position: atomsEmbroideriesDispatcher('position'),
      color: atomsEmbroideriesDispatcher('color'),
      shadowColor: atomsEmbroideriesDispatcher('shadowColor'),
      edgeColor: atomsEmbroideriesDispatcher('edgeColor'),
      content: (selected: string, i?: number) => {
        if (i === undefined) return
        handleContentAndNum(selected, i)
      }
    }
  }
}

export const cautionGenerator = (
  condition: boolean,
  key: 'position' | 'typeFace' | 'contents' | 'shadowColor' | 'edgeColor',
  t: I18NText
): string => {
  if (condition) {
    switch (key) {
      case 'position':
        return t.CAUTION_EMBROIDERY_POSITION
      case 'contents':
        return t.CAUTION_EMBROIDERY_CONTENTS
      case 'typeFace':
        return t.CAUTION_EMBROIDERY_TYPEFACE
      case 'shadowColor':
      case 'edgeColor':
        return t.CAUTION_EMBROIDERY_SHADOW_EDGE_COLOR
    }
  } else {
    return ''
  }
}
